/**
 * @version 1.0
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://gitbook.kelnik.ru/main/front-end/components/popup.html#facade documentation
 */

/**
 * DEPENDENCIES
 */
import {
    AjaxPopup,
    ConstructorPopup,
    ContentPopup,
    GalleryPopup,
    JsonPopup,
    SearcherFormPopup,
    StaticPopup,
    StaticPopupWithHash,
    StaticPopupWithHashDynamicNested,
    StaticPopupWithMap,
    StaticPopupWithMedia,
    Wishes
} from './popup';

/* eslint-disable max-lines-per-function */
export class PopupFacade {
    constructor(options = {}) {
        this.openBtns = options.openButtons;
        this.popupsGroups = {
            href: {
                Construct: StaticPopupWithHash,
                popups   : {},
                options  : {
                    title: options.title
                }
            },
            hrefHashDynamicNested: {
                Construct: StaticPopupWithHashDynamicNested,
                popups   : {},
                options  : {
                    title: options.title
                }
            },
            map: {
                Construct: StaticPopupWithMap,
                popups   : {},
                options  : {
                    title: options.title
                }
            },
            hrefNoHash: {
                Construct: StaticPopup,
                popups   : {},
                options  : {
                    title   : options.title,
                    onCreate: options.onCreate,
                    onClose : options.onClose
                }
            },
            ajax: {
                Construct: AjaxPopup,
                popups   : {},
                options  : {
                    title: options.title
                }
            },
            ajaxHref: {
                Construct: ContentPopup,
                popups   : {},
                options  : {
                    removeDelay         : options.removeDelay,
                    closeButtonAriaLabel: options.closeButtonAriaLabel,
                    onCreate            : options.onCreate
                }
            },
            media: {
                Construct: StaticPopupWithMedia,
                popups   : {},
                options  : {
                    modify     : options.modify,
                    removeDelay: options.removeDelay
                }
            },
            gallery: {
                Construct: GalleryPopup,
                popups   : {},
                options  : {
                    modify     : options.modify,
                    removeDelay: options.removeDelay
                }
            },
            constructor: {
                Construct: ConstructorPopup,
                popups   : {},
                options  : {
                    modify: options.modify
                }
            },
            json: {
                Construct: JsonPopup,
                popups   : {},
                options  : {
                    modify: options.modify
                }
            },
            saleJson: {
                Construct: JsonPopup,
                popups   : {},
                options  : {
                    modify: options.modify,
                    sale  : true
                }
            },
            searcherForm: {
                Construct: SearcherFormPopup,
                popups   : {},
                options  : {
                    parentPopup: options.parentPopup,
                    title      : options.title
                }
            },
            wishes: {
                Construct: Wishes,
                popups   : {},
                options  : {
                    title: options.title,
                    wish : options.wish
                }
            },
            Cookies: {
                Construct: StaticPopup,
                popups   : {},
                options  : {
                    removeDelay: options.removeDelay,
                    modify     : 'popup_theme_cookies'
                }
            }

        };

        this._checkOpenButtonsExistence();
    }
    /* eslint-enable max-lines-per-function */

    /**
     * @Public
     * Входная точка проекта
     */
    init() {
        this._groupPopupsByType();
        this._initPopups();
    }

    /**
     * @Private
     * Проверка на передачу наличие входнх данных
     */
    _checkOpenButtonsExistence() {
        if (!this.openBtns.length) {
            throw new Error('There is no PopupFacade open buttons in constructor');
        }
    }

    /**
     * @Private
     * Группировка кнопок по названиям ссылок внутри групп
     * @param {Node} button - кнопка для открытия попапа
     * @param {String} group - название группы, к которой относится попап
     * @param {String} link - ссылка на контент попапа
     */
    _addToGroup(button, group, link) {
        if (!this.popupsGroups[group].popups[button.dataset[link]]) {
            this.popupsGroups[group].popups[button.dataset[link]] = [];
        }

        this.popupsGroups[group].popups[button.dataset[link]].push(button);
    }

    /**
     * @Private
     * Группировка кнопок по дата атрибутам
     *
     */
    _groupPopupsByType() {
        this.openBtns.forEach((button) => {
            if (button.hasAttribute('data-gallery')) {
                this._addToGroup(button, 'gallery', 'href');
            } else if (button.hasAttribute('data-href') && button.hasAttribute('data-dynamic-nested')) {
                this._addToGroup(button, 'hrefHashDynamicNested', 'href');
            } else if (button.hasAttribute('data-href') && button.hasAttribute('data-no-hash')) {
                this._addToGroup(button, 'hrefNoHash', 'href');
            } else if (button.hasAttribute('data-href') && button.hasAttribute('data-map')) {
                this._addToGroup(button, 'map', 'href');
            } else if (button.hasAttribute('data-href') && button.hasAttribute('data-ajax')) {
                this._addToGroup(button, 'ajaxHref', 'href');
            } else if (button.hasAttribute('data-href')) {
                this._addToGroup(button, 'href', 'href');
            } else if (button.hasAttribute('data-ajax')) {
                this._addToGroup(button, 'ajax', 'ajax');
            } else if (button.hasAttribute('data-media')) {
                this._addToGroup(button, 'media', 'media');
            } else if (button.hasAttribute('data-constructor')) {
                this._addToGroup(button, 'constructor', 'constructor');
            } else if (button.hasAttribute('data-json') && button.hasAttribute('data-sale')) {
                this._addToGroup(button, 'saleJson', 'saleJson');
            } else if (button.hasAttribute('data-json')) {
                this._addToGroup(button, 'json', 'json');
            } else if (button.hasAttribute('data-flat-id') && button.hasAttribute('data-flat-plan')) {
                this._addToGroup(button, 'searcherForm', 'searcherForm');
            } else if (button.hasAttribute('data-wishes')) {
                this._addToGroup(button, 'wishes', 'wishes');
            }
        });
    }

    /**
     * @Private
     * Инициализация нужных попапов в зависимости от групп и ссылок кнопок
     *
     */
    _initPopups() {
        for (const group in this.popupsGroups) {
            if (Object.prototype.hasOwnProperty.call(this.popupsGroups, group)) {
                for (const popup in this.popupsGroups[group].popups) {
                    if (Object.prototype.hasOwnProperty.call(this.popupsGroups[group].popups, popup)) {
                        Object.assign(this.popupsGroups[group].options, {
                            openButtons: this.popupsGroups[group].popups[popup]
                        });

                        new this.popupsGroups[group].Construct(this.popupsGroups[group].options).init();
                    }
                }
            }
        }
    }
}
